// ── ADMIN: USERS ─────────────────────────────────────────────────────
// toggleActive() (deactivate/reactivate a non-dual admin) is a real import
// from admin-invitations.js, wired via VIEW_INIT below instead of
// window/onclick. userModal's Save button is a special case: its click
// behavior is reassigned dynamically at runtime by checkExistingParent()
// via `btn.onclick = fn` (cycling between saveUser/promoteToAdmin/disabled
// as the typed email matches an existing account or not). Its *initial*
// wiring uses that same `.onclick =` mechanism rather than addEventListener
// — attaching both would fire the button's action twice on click, since
// property assignment doesn't clear listeners added via addEventListener.
import { apiFetch } from '../core/api.js';
import { DB } from '../core/state.js';
import { isAdmin, isDual, isValidEmail } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, refresh } from '../core/routing.js';
import { toggleActive } from './admin-invitations.js';

VIEWS['a-users'] = () => {
  const allAdmins = DB.users.filter(u => isAdmin(u));
  return `
    <div class="page-head">
      <h2 class="serif">Users</h2>
    </div>
    <div class="card">
      <div class="card-hd">Admin users (${allAdmins.length}) <button class="btn-ghost btn-sm" data-action="add-user">+ Add user</button></div>
      <div class="tbl-wrap"><table>
        <tr><th>Name</th><th>Email</th><th>Phone</th><th>Access level</th><th>Also a parent?</th><th>Status</th><th></th></tr>
        ${allAdmins.map(u => `<tr>
          <td><button class="tbl-link" data-action="edit-user" data-account="${u.id}">${esc(u.firstName)} ${esc(u.lastName)}</button></td>
          <td>${esc(u.email)}</td>
          <td>${esc(u.phone)}</td>
          <td><span class="pill ${u.adminRole === 'view_only' ? 'pill-off' : 'pill-amber'}">${u.adminRole === 'view_only' ? 'View only' : 'Full access'}</span></td>
          <td>${isDual(u) ? '<span class="pill pill-paid">Yes</span>' : '<span style="color:var(--muted);font-size:12px">No</span>'}</td>
          <td><span class="pill ${u.active ? 'pill-active' : 'pill-off'}">${u.active ? 'Active' : 'Deactivated'}</span></td>
          <td style="white-space:nowrap;text-align:right">
            ${isDual(u)
              ? `<button class="btn-ghost btn-sm btn-danger" data-action="remove-admin-access" data-account="${u.id}">Remove admin access</button>`
              : `<button class="btn-ghost btn-sm ${u.active ? 'btn-danger' : ''}" data-action="toggle-active" data-account="${u.id}">${u.active ? 'Deactivate' : 'Reactivate'}</button>`}
          </td>
        </tr>`).join('')}
      </table></div>
    </div>`;
};

VIEW_INIT['a-users'] = () => {
  document.querySelectorAll('[data-action="add-user"]').forEach(btn => {
    btn.addEventListener('click', () => userModal());
  });
  document.querySelectorAll('[data-action="edit-user"]').forEach(btn => {
    btn.addEventListener('click', () => userModal(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="remove-admin-access"]').forEach(btn => {
    btn.addEventListener('click', () => removeAdminAccess(btn.dataset.account));
  });
  document.querySelectorAll('[data-action="toggle-active"]').forEach(btn => {
    btn.addEventListener('click', () => toggleActive(btn.dataset.account));
  });
};

// Remove staff access from a dual account, leaving the parent account fully intact.
function removeAdminAccess(userId) {
  const u = DB.users.find(x => x.id === userId);
  if (!u) return;
  openModal(`
    <h3>Remove admin access</h3>
    <p class="card-intro">Remove staff access from <b style="color:var(--cream)">${esc(u.firstName)} ${esc(u.lastName)}</b>? Their parent account, billing history, and enrolled performers stay exactly as they are — only the admin role is removed.</p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="removeAdminAccessBtn" style="flex:1;background:#f87171">Remove admin access</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('removeAdminAccessBtn').addEventListener('click', () => confirmRemoveAdminAccess(u.id));
}
async function confirmRemoveAdminAccess(userId) {
  try {
    await apiFetch('/accounts/' + userId + '/remove-admin', { method: 'POST' });
    const u = DB.users.find(x => x.id === userId);
    if (u) { u.roles = (u.roles || []).filter(r => r !== 'admin'); delete u.adminRole; }
    closeModal(); refresh();
    toast(`${u ? u.firstName + ' ' + u.lastName : 'User'} no longer has admin access — parent account preserved`);
  } catch (e) { toastError(e.message); }
}

function userModal(userId) {
  const u = userId ? DB.users.find(x => x.id === userId) : null;
  openModal(`
    <h3>${u ? 'Edit staff user' : 'Add staff user'}</h3>
    <div class="field-row">
      <div class="field"><label>First name</label><input type="text" id="umFirst" value="${u ? esc(u.firstName) : ''}"></div>
      <div class="field"><label>Last name</label><input type="text" id="umLast" value="${u ? esc(u.lastName) : ''}"></div>
    </div>
    <div class="field">
      <label>Email</label>
      <input type="email" id="umEmail" value="${u ? esc(u.email) : ''}">
      <span class="error-msg" id="umEmailErr"></span>
    </div>
    <div id="umExistingNotice" style="display:none;margin-bottom:12px;padding:11px 13px;background:rgba(201,168,76,0.07);border:1px solid rgba(201,168,76,0.25);border-radius:7px;font-size:13px;color:var(--gold-light);line-height:1.6"></div>
    <div class="field"><label>Phone</label><input type="tel" id="umPhone" value="${u ? esc(u.phone || '') : ''}"></div>
    <div class="field"><label>Access level</label><select id="umRole">
      <option value="full" ${u && u.adminRole !== 'view_only' ? 'selected' : ''}>Full access — can edit everything</option>
      <option value="view_only" ${u && u.adminRole === 'view_only' ? 'selected' : ''}>View only — read access, no edits</option>
    </select></div>
    ${u && isDual(u) ? `<p style="font-size:12px;color:var(--muted);margin-top:8px;line-height:1.6">This person also has a parent account with enrolled performers. Saving here updates their staff access only; their parent account and billing are unaffected.</p>` : ''}
    <button class="btn" id="umSaveBtn" style="margin-top:14px">${u ? 'Save changes' : 'Create user'}</button>`);
  document.getElementById('umEmail').addEventListener('input', e => checkExistingParent(e.target.value, u ? u.id : ''));
  // Initial click wiring uses .onclick= (not addEventListener) to match how
  // checkExistingParent() reassigns this button's behavior at runtime — see
  // this file's header comment for why mixing the two would double-fire.
  document.getElementById('umSaveBtn').onclick = () => saveUser(u ? u.id : '');
}

function checkExistingParent(emailVal, editingUserId) {
  const email  = emailVal.trim().toLowerCase();
  const notice = document.getElementById('umExistingNotice');
  const err    = document.getElementById('umEmailErr');
  const btn    = document.getElementById('umSaveBtn');
  const match  = DB.users.find(u => u.email.toLowerCase() === email && u.id !== editingUserId);

  if (!match) {
    notice.style.display = 'none';
    err.classList.remove('show');
    btn.textContent = editingUserId ? 'Save changes' : 'Create user';
    btn.onclick = () => saveUser(editingUserId);
    return;
  }

  if (isAdmin(match)) {
    // Already a staff user — block
    err.textContent = 'This email belongs to an existing staff user. Edit them from the Users table instead.';
    err.classList.add('show');
    notice.style.display = 'none';
    btn.textContent = 'Cannot create';
    btn.onclick = null;
    return;
  }

  // Existing parent account — offer to promote
  err.classList.remove('show');
  notice.style.display = 'block';
  notice.innerHTML = `<b>Existing parent account found</b> — ${esc(match.firstName)} ${esc(match.lastName)} is already in the system as a parent. Saving will add admin access to their existing account rather than creating a new one. Their billing history and enrolled performers are preserved.`;
  btn.textContent = 'Add admin access to existing account';
  btn.onclick = () => promoteToAdmin(match.id);
}

async function promoteToAdmin(userId) {
  const adminRole = document.getElementById('umRole').value;
  try {
    const updated = await apiFetch('/accounts/' + userId + '/promote', {
      method: 'POST', body: JSON.stringify({ adminRole })
    });
    const idx = DB.users.findIndex(x => x.id === userId);
    if (idx >= 0) DB.users[idx] = updated; else DB.users.push(updated);
    closeModal(); refresh(); toast(updated.firstName + ' ' + updated.lastName + ' now has admin access');
  } catch (e) { toastError(e.message); }
}

async function saveUser(userId) {
  const f   = id => document.getElementById(id).value.trim();
  const err = document.getElementById('umEmailErr');
  if (!f('umFirst')) { toast('Name is required'); return; }
  if (!isValidEmail(f('umEmail'))) { err.textContent = 'Please enter a valid email address.'; err.classList.add('show'); return; }
  err.classList.remove('show');
  try {
    if (userId) {
      const updated = await apiFetch('/accounts/' + userId, {
        method: 'PUT',
        body: JSON.stringify({ firstName: f('umFirst'), lastName: f('umLast'), email: f('umEmail'), phone: f('umPhone'), adminRole: document.getElementById('umRole').value, roles: ['admin'] })
      });
      const idx = DB.users.findIndex(x => x.id === userId);
      if (idx >= 0) DB.users[idx] = updated; else DB.users.push(updated);
      closeModal(); refresh(); toast('User updated');
    } else {
      const created = await apiFetch('/accounts', {
        method: 'POST',
        body: JSON.stringify({ firstName: f('umFirst'), lastName: f('umLast'), email: f('umEmail'), phone: f('umPhone'), adminRole: document.getElementById('umRole').value, roles: ['admin'] })
      });
      DB.users.push(created);
      closeModal(); refresh(); toast('User created — invite email sent');
    }
  } catch (e) { toastError(e.message); }
}
